import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCall } from '../../context/CallContext';
import './CallModal.css';

// Global overlay — mount jednom u App.js (van Chat.js) da dolazni poziv stigne
// bez obzira na kojoj je stranici korisnik trenutno.
const CallModal = () => {
    const { t } = useTranslation();
    const call = useCall() || {};
    const localVideoRef = useRef(null);
    const remoteVideoRef = useRef(null);
    const remoteAudioRef = useRef(null);
    const ringToneRef = useRef(null);
    const [trajanje, setTrajanje] = useState(0);

    // Napomena: sledeći hook-ovi MORAJU biti pozvani bezuslovno (pre bilo kakvog early return-a),
    // zato se destrukturira sa default vrednostima umesto `if (!call) return null` PRE hook-ova.
    const { callState = 'idle', callInfo, localStream, remoteStream, muted, cameraOff, connecting, greskaVeze,
        cancelCall, acceptCall, rejectCall, endCall, toggleMute, toggleCamera,
        snimam, snimaSagovornik, snimakZaCuvanje, toggleSnimanje, sacuvajSnimak, odbaciSnimak } = call;
    const [cuvaSnimak, setCuvaSnimak] = useState(false);
    const [greskaSnimka, setGreskaSnimka] = useState(false);

    const jeVideo = callInfo?.callType === 'video';

    // Poveži MediaStream objekte na <video>/<audio> elemente (React ne podržava srcObject kao prop).
    /* callState je u zavisnostima: <video> za sopstvenu sliku se montira tek kad
       prozor pređe u odgovarajuće stanje — ranije je efekat prošao pre toga i
       pozivalac je video crn kvadrat umesto sebe. */
    useEffect(() => {
        if (localVideoRef.current) localVideoRef.current.srcObject = localStream || null;
    }, [localStream, callState]);
    useEffect(() => {
        if (jeVideo && remoteVideoRef.current) remoteVideoRef.current.srcObject = remoteStream || null;
        if (!jeVideo && remoteAudioRef.current) remoteAudioRef.current.srcObject = remoteStream || null;
    }, [remoteStream, jeVideo]);

    /* Zvuk poziva — vezan ISKLJUČIVO za stanje, pa staje istog trenutka kad se
       stanje promeni (javljanje, odbijanje, prekid):
         incoming → zvono (melodija, public/zvuk/zvono.wav)
         outgoing → ton čekanja za pozivaoca (425 Hz, 1 s / 4 s, kao telefonska mreža)
       Ranije je ovde bio „pip" od 520 Hz koji je za svaki pisak pravio nov
       AudioContext i nikad ga nije zatvarao. Isti fajlovi su i u mobilnoj aplikaciji. */
    useEffect(() => {
        const fajl = callState === 'incoming' ? 'zvono.wav'
            : callState === 'outgoing' ? 'ton_cekanja.wav' : null;
        if (!fajl) return undefined;
        const zvuk = new Audio(`${process.env.PUBLIC_URL || ''}/zvuk/${fajl}`);
        zvuk.loop = true;
        zvuk.volume = callState === 'incoming' ? 0.9 : 0.5;
        ringToneRef.current = zvuk;
        /* Pregledač blokira zvuk dok korisnik ništa nije kliknuo na stranici —
           tada poziv i dalje stoji na ekranu, samo bez zvuka. */
        zvuk.play().catch(() => {});
        return () => {
            zvuk.pause();
            zvuk.removeAttribute('src');
            zvuk.load();
            ringToneRef.current = null;
        };
    }, [callState]);

    // Tajmer trajanja aktivnog poziva.
    useEffect(() => {
        if (callState !== 'active' || connecting) { setTrajanje(0); return; }
        const iv = setInterval(() => setTrajanje(s => s + 1), 1000);
        return () => clearInterval(iv);
    }, [callState, connecting]);

    /* Posle poziva: da li čuvati snimak (samo kad je snimanje pokrenuo ovaj korisnik). */
    const dijalogSnimka = snimakZaCuvanje && (
        <div className="call-snimak-dijalog" role="dialog" aria-modal="true" aria-labelledby="call-snimak-naslov">
            <div className="call-snimak-karta">
                <div id="call-snimak-naslov" className="call-snimak-naslov">{t('chat.snimakNaslov')}</div>
                <div className="call-snimak-tekst">
                    {t('chat.snimakPitanje', { ko: snimakZaCuvanje.peerName || '', trajanje: `${Math.floor(snimakZaCuvanje.trajanjeSek / 60)}:${String(snimakZaCuvanje.trajanjeSek % 60).padStart(2, '0')}` })}
                </div>
                {greskaSnimka && <div className="call-snimak-greska">{t('chat.snimakGreska')}</div>}
                <div className="call-snimak-dugmad">
                    <button type="button" className="btn btn-secondary" disabled={cuvaSnimak}
                        onClick={() => { setGreskaSnimka(false); odbaciSnimak(); }}>{t('chat.snimakOdbaci')}</button>
                    <button type="button" className="btn btn-success" disabled={cuvaSnimak}
                        onClick={async () => {
                            setCuvaSnimak(true); setGreskaSnimka(false);
                            const r = await sacuvajSnimak();
                            setCuvaSnimak(false);
                            if (!r.ok) setGreskaSnimka(true);
                        }}>{cuvaSnimak ? t('common.cuvanje') : t('chat.snimakSacuvaj')}</button>
                </div>
            </div>
        </div>
    );

    if (callState === 'idle') return dijalogSnimka || null;

    const formatTrajanje = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
    const inicijali = (name) => (name || '?').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();

    return (
        <div className="call-overlay">
            {callState === 'outgoing' && (
                <div className="call-card">
                    <div className="call-avatar call-avatar-pulse">{inicijali(callInfo?.peerName)}</div>
                    <div className="call-peer-name">{callInfo?.peerName}</div>
                    <div className="call-status">{t('chat.callDialing')}</div>
                    <div className="call-controls">
                        <button className="call-btn call-btn-hangup" onClick={cancelCall} title={t('chat.callCancel')}>
                            <i className="pi pi-phone" style={{ transform: 'rotate(135deg)' }} />
                        </button>
                    </div>
                </div>
            )}

            {callState === 'incoming' && (
                <div className="call-card">
                    <div className="call-avatar call-avatar-pulse">{inicijali(callInfo?.peerName)}</div>
                    <div className="call-peer-name">{callInfo?.peerName}</div>
                    <div className="call-status">
                        {jeVideo ? t('chat.callIncomingVideo') : t('chat.callIncomingAudio')}
                    </div>
                    <div className="call-controls">
                        <button className="call-btn call-btn-reject" onClick={rejectCall} title={t('chat.callReject')}>
                            <i className="pi pi-phone" style={{ transform: 'rotate(135deg)' }} />
                        </button>
                        <button className="call-btn call-btn-accept" onClick={acceptCall} title={t('chat.callAccept')}>
                            <i className="pi pi-phone" />
                        </button>
                    </div>
                </div>
            )}

            {callState === 'active' && (
                <div className={`call-active ${jeVideo ? 'call-active-video' : ''}`}>
                    {jeVideo ? (
                        <>
                            <video ref={remoteVideoRef} className="call-remote-video" autoPlay playsInline />
                            <video ref={localVideoRef} className="call-local-video" autoPlay playsInline muted />
                        </>
                    ) : (
                        <>
                            <audio ref={remoteAudioRef} autoPlay />
                            <div className="call-avatar call-avatar-lg">{inicijali(callInfo?.peerName)}</div>
                        </>
                    )}
                    {(snimam || snimaSagovornik) && (
                        <div className="call-snima-traka" role="status">
                            <span className="call-snima-tacka" aria-hidden="true" />
                            {snimam ? t('chat.snimateRazgovor') : t('chat.sagovornikSnima')}
                        </div>
                    )}
                    <div className={`call-info-bar ${jeVideo ? 'call-info-bar-video' : ''}`}>
                        <div className="call-peer-name">{callInfo?.peerName}</div>
                        <div className={`call-status${greskaVeze ? ' call-status-greska' : ''}`}>
                            {greskaVeze ? t('chat.' + greskaVeze) : connecting ? t('chat.callConnecting') : formatTrajanje(trajanje)}
                        </div>
                    </div>
                    <div className="call-controls">
                        <button className={`call-btn call-btn-toggle ${muted ? 'call-btn-toggle-off' : ''}`} onClick={toggleMute} title={t('chat.callMute')}>
                            <i className={`pi ${muted ? 'pi-microphone' : 'pi-microphone'}`} />
                        </button>
                        {jeVideo && (
                            <button className={`call-btn call-btn-toggle ${cameraOff ? 'call-btn-toggle-off' : ''}`} onClick={toggleCamera} title={t('chat.callCamera')}>
                                <i className="pi pi-video" />
                            </button>
                        )}
                        <button className={`call-btn call-btn-toggle ${snimam ? 'call-btn-snima' : ''}`}
                            onClick={toggleSnimanje} disabled={connecting || !remoteStream}
                            title={snimam ? t('chat.zaustaviSnimanje') : t('chat.snimaj')}
                            aria-pressed={!!snimam}>
                            <i className={`pi ${snimam ? 'pi-stop-circle' : 'pi-circle'}`} />
                        </button>
                        <button className="call-btn call-btn-hangup" onClick={endCall} title={t('chat.callEnd')}>
                            <i className="pi pi-phone" style={{ transform: 'rotate(135deg)' }} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CallModal;
